/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState, useRef } from 'react';
import { Button, DatePicker, Form, Input, Message, Table, TableColumnProps, Modal, Popconfirm, Select } from '@arco-design/web-react';
import dayjs from 'dayjs';
import { get, post } from '../requestNew';

import './index.scss';
import { UmsUserDevice } from './types';

const FormItem = Form.Item;
const Option = Select.Option;

// 网络类型选项
const networkTypeOptions = [
  { label: '全部', value: '' },
  { label: 'WiFi', value: 'wifi' },
  { label: '4G', value: '4g' },
  { label: '5G', value: '5g' },
  { label: '3G', value: '3g' },
  { label: '2G', value: '2g' },
  { label: '无网络', value: 'none' },
  { label: '未知', value: 'unknown' }
];

function UserDevice() {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<UmsUserDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const formRef = useRef<UmsUserDevice>({});

  const columns: TableColumnProps[] = [
    {
      title: '序号',
      width: 60,
      render: (_, record, i) => (pageNum - 1) * pageSize + i + 1
    },
    {
      title: 'openId',
      dataIndex: 'openId',
      width: 200,
      render: text => text || '-'
    },
    {
      title: 'unionid',
      dataIndex: 'unionid',
      width: 200,
      render: text => text || '-'
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      width: 150,
      render: text => text || '-'
    },
    {
      title: '设备型号',
      dataIndex: 'model',
      width: 150,
      render: text => text || '-'
    },
    {
      title: '网络类型',
      dataIndex: 'networkType',
      width: 120,
      render: text => networkTypeOptions.find(item => item.value === text)?.label || '-'
    },
    {
      title: '客户端平台',
      dataIndex: 'platform',
      width: 120,
      render: text => text || '-'
    },
    {
      title: '操作系统',
      dataIndex: 'operatingSystem',
      width: 150,
      render: text => text || '-'
    },
    {
      title: '扩展字段',
      dataIndex: 'extension',
      width: 200,
      render: text => text || '-'
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record: UmsUserDevice) => (
        <>
          <Button className="btn" type="text" onClick={() => edit(record)} loading={loading}>
            编辑
          </Button>
          <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => del(record)}>
            <Button className="btn" type="text" status="danger" loading={loading}>
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  const getList = (reset?: boolean) => {
    setLoading(true);
    if (reset) {
      setPageNum(1);
    }
    const obj: any = form.getFieldsValue();
    const params: any = {
      ...obj,
      pageSize,
      pageNum: reset ? 1 : pageNum
    };

    // 从日期范围中提取开始时间和结束时间
    if (obj.dateRange && Array.isArray(obj.dateRange) && obj.dateRange.length === 2) {
      params.reportTimeStart = dayjs(obj.dateRange[0]).format('YYYY-MM-DD HH:mm:ss');
      params.reportTimeEnd = dayjs(obj.dateRange[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    // 删除 dateRange 字段，避免提交到后端
    delete params.dateRange;

    // 处理网络类型参数，空字符串时不传递
    if (params.networkType === '' || !params.networkType) {
      delete params.networkType;
    }

    get('/userDevice/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total: totalCount } = data || {};
          setTotal(totalCount || 0);
          setDataList(list || []);
        } else {
          Message.error(message || '请求失败');
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReset = () => {
    form.resetFields();
    setPageNum(1);
    setPageSize(20);
    const params: any = {
      pageSize: 20,
      pageNum: 1
    };
    setLoading(true);
    get('/userDevice/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total: totalCount } = data || {};
          setTotal(totalCount || 0);
          setDataList(list || []);
        } else {
          Message.error(message || '请求失败');
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const edit = (record: UmsUserDevice) => {
    formRef.current = record;
    editForm.setFieldsValue(record);
    setVisible(true);
  };

  const editCancel = () => {
    formRef.current = {};
    setVisible(false);
    editForm.resetFields();
  };

  const onEdit = () => {
    editForm.validate().then((formData) => {
      setConfirmLoading(true);
      const data = {
        ...formRef.current,
        ...formData
      };
      post('/userDevice/update', data)
        .then((res: any) => {
          const { code, message } = res || {};
          if (code === 200) {
            Message.success('编辑成功！');
            getList();
            editCancel();
          } else {
            Message.error(message || '编辑失败');
          }
        })
        .catch(e => {
          Message.error('网络错误!');
          console.log(e);
        })
        .finally(() => {
          setConfirmLoading(false);
        });
    }).catch(() => {
      Message.error('请填写完整信息');
    });
  };

  const del = (record: UmsUserDevice) => {
    if (!record.id) {
      Message.error('ID不存在！');
      return;
    }
    setLoading(true);
    get(`/userDevice/delete/${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          getList();
        } else {
          Message.error(message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getList(false);
  }, [pageNum, pageSize]);

  return (
    <div className="user-device table-flex">
      <Form style={{ width: '100%' }} autoComplete="off" layout="inline" form={form}>
        <FormItem label="openId" field="openId">
          <Input style={{ width: 200 }} placeholder="请输入" allowClear />
        </FormItem>
        <FormItem label="ip地址" field="ipAddress">
          <Input style={{ width: 150 }} placeholder="请输入" allowClear />
        </FormItem>
        <FormItem label="设备型号" field="model">
          <Input style={{ width: 150 }} placeholder="请输入" allowClear />
        </FormItem>
        <FormItem label="网络类型" field="networkType">
          <Select style={{ width: 120 }} placeholder="请选择" allowClear>
            {networkTypeOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem label="时间范围" field="dateRange">
          <DatePicker.RangePicker
            style={{ width: 300 }}
            showTime
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </FormItem>
        <FormItem>
          <Button type="primary" onClick={() => getList(true)} loading={loading}>
            查询
          </Button>
        </FormItem>
        <FormItem>
          <Button type="outline" onClick={handleReset} loading={loading}>
            重置
          </Button>
        </FormItem>
      </Form>
      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          loading={loading}
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            sizeOptions: [20, 50, 100],
            onChange: (num, size) => {
              setPageNum(num);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title="编辑用户设备信息"
        visible={visible}
        onCancel={editCancel}
        onOk={onEdit}
        unmountOnExit
        style={{ width: 600 }}
        confirmLoading={confirmLoading}
      >
        <Form form={editForm} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <FormItem label="ID" field="id" hidden>
            <Input />
          </FormItem>
          <FormItem label="openId" field="openId">
            <Input placeholder="请输入openId" />
          </FormItem>
          <FormItem label="unionid" field="unionid">
            <Input placeholder="请输入unionid" />
          </FormItem>
          <FormItem label="IP地址" field="ipAddress">
            <Input placeholder="请输入IP地址" />
          </FormItem>
          <FormItem label="设备型号" field="model">
            <Input placeholder="请输入设备型号" />
          </FormItem>
          <FormItem label="网络类型" field="networkType">
            <Select placeholder="请选择网络类型" allowClear>
              {networkTypeOptions.filter(opt => opt.value !== '').map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label="客户端平台" field="platform">
            <Input placeholder="请输入客户端平台" />
          </FormItem>
          <FormItem label="操作系统" field="operatingSystem">
            <Input placeholder="请输入操作系统" />
          </FormItem>
          <FormItem label="扩展字段" field="extension">
            <Input.TextArea placeholder="请输入扩展字段" rows={3} />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default UserDevice;
